import { Menu } from '@mui/material';
import { Contract, CONTRACT_STATUSES, INTEGRATION_PROVIDERS, type PlanBillingData } from '@vy/types';
import { FC } from 'react';
import { findIntegrationByProviderInEntities } from '@vy/services';
import { useActivatedIntegration } from '../../../../../hooks/use-activated-integration';
import { hasNetSuiteIntegration } from '../../../../../utils/integration-utils';
import { ActivateContractMenuItem } from '../../ContractPageHeaderMenu/contract-page-header-menu-components/ActivateContractMenuItem';
import { DeleteContractMenuItem } from '../../ContractPageHeaderMenu/contract-page-header-menu-components/DeleteContractMenuItem';
import { MenuItemNetSuiteExportContract } from '../../ContractPageHeaderMenu/contract-page-header-menu-components/MenuItemNetSuiteExportContract';
import { MenuItemRenewOptions } from '../../ContractPageHeaderMenu/contract-page-header-menu-components/MenuItemRenewOptions/MenuItemRenewOptions';
import { MenuItemReviseContract } from '../../ContractPageHeaderMenu/contract-page-header-menu-components/MenuItemReviseContract';
import { MenuItemTerminateContract } from '../../ContractPageHeaderMenu/contract-page-header-menu-components/MenuItemTerminateContract';
import { MenuItemUpdateContract } from '../../ContractPageHeaderMenu/contract-page-header-menu-components/MenuItemUpdateContract';
import {
  DEFAULT_MENU_ANCHOR_ORIGIN,
  DEFAULT_MENU_TRANSFORM_ORIGIN,
} from '../../ContractPageHeaderMenu/contract-page-header-menu-consts/contract-page-default-menu';
import { isNotNullOrUndefined } from '@vy/utils';
import { CreateContractDialog } from '../../../../plans/GetStartedPlansPage/components/CardsSections/components/CreateContractDialog';
import { useDialog } from '../../../../../hooks/use-dialog';
import { CloneContractMenuItem } from '../../ContractPageHeaderMenu/contract-page-header-menu-components/CloneContractMenuItem';

type ContractDotMenuProps = {
  contract: Contract;
  isOpen: boolean;
  anchorElement: HTMLElement | null;
  handleMenuClose: () => void;
  billingData: PlanBillingData;
  isContractEditable: boolean;
  isContractHasPhases?: boolean;
  isContractWasRenewed: boolean;
};

export const ContractDotMenu: FC<ContractDotMenuProps> = ({
  contract,
  isOpen,
  anchorElement,
  handleMenuClose,
  billingData,
  isContractEditable,
  isContractHasPhases,
  isContractWasRenewed,
}) => {
  const {
    id: contractId,
    startDate: contractStartDate,
    endDate: contractEndDate,
    customer,
    status,
    plan: { name: contractName, id: planId },
    integrationEntities,
  } = contract;

  const {
    isDialogOpen: isCreateContractDialogOpen,
    handleOpen: handleCreateContractDialogOpen,
    handleClose: handleCreateContractDialogClose,
  } = useDialog();
  const { name: customerName, id: customerId, integrationEntities: customerIntegrationEntities } = customer;
  const isActive = status === CONTRACT_STATUSES.Active;
  const isTerminated = status === CONTRACT_STATUSES.Terminated;

  const isNetSuiteConnected = useActivatedIntegration(INTEGRATION_PROVIDERS.netSuite);
  const hasNetSuiteSyncStatus = hasNetSuiteIntegration(integrationEntities);

  const customerNetSuiteEntity = isNotNullOrUndefined(customerIntegrationEntities)
    ? findIntegrationByProviderInEntities(customerIntegrationEntities, INTEGRATION_PROVIDERS.netSuite)
    : null;
  const customerErpId = customerNetSuiteEntity?.integrationExternalId;

  return (
    <>
      <Menu
        anchorOrigin={DEFAULT_MENU_ANCHOR_ORIGIN}
        transformOrigin={DEFAULT_MENU_TRANSFORM_ORIGIN}
        open={isOpen}
        anchorEl={anchorElement}
        onClose={handleMenuClose}
      >
        <MenuItemUpdateContract
          contractId={contractId}
          isEditable={isContractEditable}
          contractStartDate={contractStartDate}
          billingInterval={billingData.billingInterval}
          disabled={isTerminated || isContractHasPhases || isContractWasRenewed}
        />

        <MenuItemReviseContract
          contractId={contractId}
          contractStartDate={contractStartDate}
          contractEndDate={contractEndDate}
          disabled={isTerminated || isContractEditable}
        />

        <CloneContractMenuItem onClick={handleCreateContractDialogOpen} />

        <MenuItemRenewOptions contractId={contractId} isAutoRenewal={billingData.autoRenewal} />

        {isNetSuiteConnected && (
          <MenuItemNetSuiteExportContract
            contractId={contractId}
            customerId={customerId}
            planId={planId}
            disabled={isTerminated}
            isSynced={hasNetSuiteSyncStatus}
            customerErpId={customerErpId}
          />
        )}

        <ActivateContractMenuItem disabled={isTerminated} contractId={contractId} isActive={isActive} />

        <MenuItemTerminateContract
          contractId={contractId}
          customerName={customerName}
          isActive={isActive}
          contractStartDate={contractStartDate}
          contractEndDate={contractEndDate}
        />

        
        <DeleteContractMenuItem
          contractId={contractId}
          contractName={contractName}
          disabled={!isContractEditable}
        />
      </Menu>
      <CreateContractDialog
        isOpen={isCreateContractDialogOpen}
        onClose={handleCreateContractDialogClose}
        cloneFromPlanId={contract.plan.id}
        initialCustomerId={contract.customer.id}
        label="Clone Contract and Assign to a Customer"
      />
    </>
  );
};
